import { Component, HostListener, signal } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Icon } from "./icon";
import { Reveal } from "./reveal";

interface Shot {
  src: string;
  caption: string;
  place: string;
  span?: string;
}

const SHOTS: Shot[] = [
  {
    src: "./hero-bg.png",
    caption: "Sigiriya Rock Fortress at dusk",
    place: "Sigiriya",
    span: "sm:col-span-2 sm:row-span-2",
  },
  {
    src: "./home-grid-2.png",
    caption: "Bronze Buddhas by Beira Lake",
    place: "Colombo",
  },
  {
    src: "./home-grid-3.png",
    caption: "The Temple of the Sacred Tooth Relic",
    place: "Kandy",
  },
  {
    src: "./tour-galle-1.png",
    caption: "The ramparts and lighthouse",
    place: "Galle Fort",
  },
  {
    src: "./home-grid-5.png",
    caption: "The highland line toward Ella",
    place: "Nine Arch Bridge",
  },
  {
    src: "./home-grid-1.png",
    caption: "Waterfalls in the tea highlands",
    place: "Central Highlands",
  },
  {
    src: "./tour-ella-1.png",
    caption: "Rice paddies climbing to Ella Rock",
    place: "Ella",
  },
  {
    src: "./home-grid-4.png",
    caption: "An infinity edge over the Indian Ocean",
    place: "South Coast",
    span: "sm:col-span-2",
  },
  {
    src: "./welcome-bg.png",
    caption: "Palms along the southern shoreline",
    place: "Mirissa",
  },
  {
    src: "./tour-kandy-1.png",
    caption: "The Temple of the Tooth by day",
    place: "Kandy",
  },
  {
    src: "./tour-sigiriya-1.png",
    caption: "The lion rock, seen from the jungle floor",
    place: "Sigiriya",
  },
];

@Component({
  selector: "app-gallery",
  imports: [RouterLink, Icon, Reveal],
  template: `
    <section class="grain relative pt-40 pb-20 surface-ink overflow-hidden">
      <div
        class="mesh-blob mesh-blob-a w-[420px] h-[420px] -top-32 -right-24 bg-saffron/15"
      ></div>
      <div
        class="mesh-blob mesh-blob-b w-[300px] h-[300px] bottom-0 left-0 bg-vermilion/10"
      ></div>
      <div class="container relative z-10">
        <div class="flex items-center gap-2 mb-6" appReveal="fade">
          <div class="w-8 h-[2px] bg-saffron"></div>
          <span
            class="text-saffron font-bold uppercase tracking-[0.3em] text-[10px]"
            >Visual Journey</span
          >
        </div>
        <h1
          class="text-6xl md:text-8xl font-serif leading-[0.9] tracking-tight"
          appReveal
          [revealDelay]="0.1"
        >
          Captured <span class="italic font-light text-paper/50">Moments</span>
        </h1>
        <p
          class="max-w-xl text-paper/60 leading-relaxed text-lg mt-6"
          appReveal
          [revealDelay]="0.2"
        >
          A field notebook, in photographs - every frame shot on routes we run
          ourselves.
        </p>
      </div>
    </section>

    <section class="py-24">
      <div class="container">
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-5 auto-rows-[220px]">
          @for (shot of shots; track shot.src; let i = $index) {
            <button
              type="button"
              (click)="open(i)"
              class="relative overflow-hidden rounded-[1.5rem] group text-left"
              [class]="shot.span ?? ''"
              appReveal="fade"
              [revealDelay]="Math.min(i, 6) * 0.05"
            >
              <img
                [src]="shot.src"
                [alt]="shot.caption"
                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                referrerpolicy="no-referrer"
              />
              <div
                class="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/0 to-ink/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              ></div>
              <div
                class="absolute bottom-0 left-0 right-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500"
              >
                <div class="route-code text-[10px] text-saffron mb-1">
                  {{ shot.place }}
                </div>
                <div class="text-paper text-sm font-medium">
                  {{ shot.caption }}
                </div>
              </div>
              <div
                class="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center bg-paper/90 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <app-icon name="zoom-in" [size]="15" />
              </div>
            </button>
          }
        </div>

        <div class="mt-24 text-center" appReveal="fade">
          <p class="text-ink/50 mb-6">Want to shoot these frames yourself?</p>
          <a routerLink="/tours" class="btn-vermilion mx-auto">
            Browse the Routes <app-icon name="arrow-right" [size]="16" />
          </a>
        </div>
      </div>
    </section>

    @if (activeIndex() !== null) {
      <div
        class="fixed inset-0 z-[200] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
      >
        <button
          type="button"
          aria-label="Close gallery"
          (click)="close()"
          class="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center text-paper/70 hover:text-paper glass-ink transition-all"
        >
          <app-icon name="close" [size]="20" />
        </button>
        <button
          type="button"
          aria-label="Previous photo"
          (click)="prev()"
          class="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-paper/70 hover:text-paper glass-ink transition-all"
        >
          <app-icon name="chevron-left" [size]="20" />
        </button>
        <button
          type="button"
          aria-label="Next photo"
          (click)="next()"
          class="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-paper/70 hover:text-paper glass-ink transition-all"
        >
          <app-icon name="chevron-right" [size]="20" />
        </button>

        <figure class="max-w-4xl w-full">
          <img
            [src]="shots[activeIndex()!].src"
            [alt]="shots[activeIndex()!].caption"
            class="w-full max-h-[70vh] object-contain rounded-[1.5rem]"
            referrerpolicy="no-referrer"
          />
          <figcaption class="mt-6 text-center">
            <div class="route-code text-saffron text-xs mb-1">
              {{ shots[activeIndex()!].place }}
            </div>
            <div class="text-paper text-lg font-serif">
              {{ shots[activeIndex()!].caption }}
            </div>
          </figcaption>
        </figure>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class Gallery {
  shots = SHOTS;
  Math = Math;
  activeIndex = signal<number | null>(null);

  open(i: number) {
    this.activeIndex.set(i);
  }

  close() {
    this.activeIndex.set(null);
  }

  next() {
    this.activeIndex.update((i) =>
      i === null ? null : (i + 1) % this.shots.length,
    );
  }

  prev() {
    this.activeIndex.update((i) =>
      i === null ? null : (i - 1 + this.shots.length) % this.shots.length,
    );
  }

  @HostListener("document:keydown", ["$event"])
  onKeydown(e: KeyboardEvent) {
    if (this.activeIndex() === null) return;
    if (e.key === "Escape") this.close();
    if (e.key === "ArrowRight") this.next();
    if (e.key === "ArrowLeft") this.prev();
  }
}
